import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../../styles/PageHeader.css';

interface BreadcrumbItem {
  label: string;
  to?: string;
}

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: BreadcrumbItem[];
  actions?: React.ReactNode;
  children?: React.ReactNode;
  /** If provided, title becomes editable on click */
  onTitleChange?: (newTitle: string) => Promise<void>;
  /** Show editing is in progress */
  isTitleSaving?: boolean;
  /** Explicit parent route for back navigation (NOT navigate(-1)) */
  backTo?: string;
  /** Label shown next to back arrow */
  backLabel?: string;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  breadcrumbs,
  actions,
  children,
  onTitleChange,
  isTitleSaving,
  backTo,
  backLabel,
}) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update edit value when title changes externally
  useEffect(() => {
    if (!isEditing) {
      setEditValue(title);
    }
  }, [title, isEditing]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleTitleClick = () => {
    if (onTitleChange && !isTitleSaving) {
      setEditValue(title);
      setIsEditing(true);
    }
  };

  const handleSave = async () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== title && onTitleChange) {
      await onTitleChange(trimmed);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(title);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };
  return (
    <div className="page-header">
      {/* Animated blobs */}
      <div className="page-header__blobs">
        <div className="page-header__blob page-header__blob--light" />
        <div className="page-header__blob page-header__blob--accent" />
        <div className="page-header__blob page-header__blob--cyan" />
      </div>

      {/* Shimmer effect */}
      <div className="page-header__shimmer" />

      {/* Content */}
      <div className="page-header__content">
        {/* Row 1: breadcrumbs + title + actions — all on one line */}
        <div className="page-header__row">
          <div className="page-header__nav">
            {backTo && (
              <button
                onClick={() => navigate(backTo)}
                className="page-header__back-btn"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-secondary, rgba(255,255,255,0.7))',
                  cursor: 'pointer',
                  fontSize: '13px',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  marginRight: '8px',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--hover-bg, rgba(255,255,255,0.1))'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
                {backLabel || 'Back'}
              </button>
            )}
            {breadcrumbs && breadcrumbs.length > 0 && (
              <nav className="page-header__breadcrumbs">
                {breadcrumbs.map((crumb, idx) => (
                  <React.Fragment key={idx}>
                    {idx > 0 && (
                      <svg className="page-header__breadcrumb-sep" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 5l7 7-7 7" />
                      </svg>
                    )}
                    {crumb.to ? (
                      <Link to={crumb.to} className="page-header__breadcrumb-link">{crumb.label}</Link>
                    ) : (
                      <span className="page-header__breadcrumb-current">{crumb.label}</span>
                    )}
                  </React.Fragment>
                ))}
                {/* Separator before title */}
                <svg className="page-header__breadcrumb-sep" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 5l7 7-7 7" />
                </svg>
              </nav>
            )}
            {isEditing ? (
              <div className="page-header__title-edit">
                <input
                  ref={inputRef}
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={handleSave}
                  className="page-header__title-input"
                  disabled={isTitleSaving}
                />
                {isTitleSaving && (
                  <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                )}
              </div>
            ) : (
              <h1
                className={`page-header__title ${onTitleChange ? 'page-header__title--editable' : ''}`}
                title={title}
              >
                {title}
                {onTitleChange && (
                  <span onClick={handleTitleClick} role="button" title="Rename" className="page-header__edit-icon-wrapper">
                    <svg className="page-header__edit-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </span>
                )}
              </h1>
            )}
          </div>
          {actions && <div className="page-header__actions">{actions}</div>}
        </div>

        {/* Row 2: subtitle */}
        {subtitle && <p className="page-header__subtitle">{subtitle}</p>}

        {children}
      </div>
    </div>
  );
};

export default PageHeader;
